import {
	DateString,
	DetailsBox,
	DropOver,
	FormattedText,
	RawHtml,
	Space,
	WeekViewCardStyle
} from "client/Components";
import { useCallbackWithoutArguments } from "client/Hooks";
import { isNotNilOrEmpty } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement } from "react";
import {
	LessonKind,
	MagisterAppointment,
	StatusKind
} from "client/Api/Workspace/LiveInformation/Magister/magisterLiveInformation";
import { TimeZone } from "client/DateAndTime/timezones";

export type AppointmentDetailsProps = {
	target: HTMLElement;
	onClose(): void;
	appointment: MagisterAppointment;
	variant: WeekViewCardStyle | undefined;
	onNavigate: () => void;
	timezone: TimeZone;
};

export const AppointmentDetails = (props: AppointmentDetailsProps): ReactElement => {
	const {
		target,
		onClose,
		appointment,
		variant,
		onNavigate,
		timezone
	} = props;

	const navigateHandler: () => void = useCallbackWithoutArguments(onNavigate);

	return (
		<DropOver
			target={target}
			onClose={onClose}
		>
			<DetailsBox
				variant={variant}
				title={appointment.description}
				onClose={onClose}
			>
				<DetailsBox.List>
					{appointment.status === StatusKind.Cancelled && (
						<DetailsBox.Item
							icon={{
								kind: "font",
								name: "xmark",
								color: "danger"
							}}
						>
							{localize("_Cancelled")}
						</DetailsBox.Item>
					)}

					{(appointment.lessonKind === LessonKind.Test
						|| appointment.lessonKind === LessonKind.Examination
						|| appointment.lessonKind === LessonKind.OralExamination
						|| appointment.lessonKind === LessonKind.WrittenExamination) && (
							<DetailsBox.Item
								icon={{
									kind: "font",
									name: "file-lines",
									color: "warning"
								}}
							>
								{localize(examToLocalizationKey[appointment.lessonKind])}
							</DetailsBox.Item>
					)}

					<DetailsBox.Divider />

					<DetailsBox.Item icon="clock">
						<DateString
							value={appointment.startDateTime}
							format="DMY"
							timezone={timezone}
						/>
						<Space />
						<DateString
							value={appointment.startDateTime}
							format="T"
							timezone={timezone}
						/>
						<Space />
						-
						<Space />
						<DateString
							value={appointment.finishDateTime}
							format="T"
							timezone={timezone}
						/>
					</DetailsBox.Item>

					<DetailsBox.Item icon="location-dot">
						{appointment.location}
					</DetailsBox.Item>

					{appointment.teacherShortName && (
						<DetailsBox.Item icon="user-tie">
							{appointment.teacherShortName}
						</DetailsBox.Item>
					)}

					<DetailsBox.Divider />

					{appointment.status === StatusKind.Changed && (
						<>
							<DetailsBox.Item icon="triangle-exclamation">
								{localize("_Changed")}
							</DetailsBox.Item>

							<DetailsBox.Divider />
						</>
					)}

					{isNotNilOrEmpty(appointment.message) && (
						<DetailsBox.Item icon="circle-info">
							<RawHtml html={appointment.message} isInline />
						</DetailsBox.Item>
					)}

					<DetailsBox.Anchor
						onClick={navigateHandler}
					>
						<FormattedText
							text={localize("_OpenInAppName")}
							thingsToReplace={[
								{ what: "{0}", filler: localize("_Magister") }
							]}
						/>
					</DetailsBox.Anchor>
				</DetailsBox.List>
			</DetailsBox>
		</DropOver>
	);
};
AppointmentDetails.displayName = "AppointmentDetails";

type ExamKind =
	| LessonKind.Test
	| LessonKind.Examination
	| LessonKind.OralExamination
	| LessonKind.WrittenExamination;

const examToLocalizationKey: { [key in ExamKind]: LocalizationKey; } = {
	[LessonKind.Test]: "_Test",
	[LessonKind.Examination]: "_Examination",
	[LessonKind.OralExamination]: "_OralExamination",
	[LessonKind.WrittenExamination]: "_WrittenExamination",
};
